import type {Editor} from '@tiptap/core';
import {CodeBlock} from '@tiptap/extension-code-block';
import {CodeBlockLowlight} from '@tiptap/extension-code-block-lowlight';
import {EditorContent, useEditor} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import css from 'highlight.js/lib/languages/css';
import html from 'highlight.js/lib/languages/xml';
import {all, createLowlight} from 'lowlight';
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import {toast} from 'react-toastify';
import {useDebouncedCallback} from 'use-debounce';
import {useHtmlIframe} from '../../../../hooks/useHtmlIFrame';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {IElementHtml} from '../../../../incase-domain/elements/html/html';
import {isHtmlQuestionReportElement, isHtmlSlottedElement} from '../../../../incase-domain/elements/html/htmlSlot';
import {showHtmlEditorModalAction} from '../../../../store/actions/html.actions';
import {showHtmlCodeEditorModal} from '../../../../store/selectors/html.selectors';
import {selectedPresentationSelector} from '../../../../store/selectors/presentation.selectors';
import {selectedSlideSelector} from '../../../../store/selectors/slide.selectors';
import {createHtmlImageUploader, htmlContainsImageDataUrl} from '../../../../utils/htmlEmbeddedImages';
import {formatHtml} from '../../../../utils/tipTap.utils';
import HtmlQuestionReportBridge from '../../../elements/text/HtmlQuestionReportBridge';
import HtmlSlottedElementPortal from '../../../elements/text/HtmlSlottedElementPortal';
import {getImageSizeErrorMessage} from '../../../../utils/imageUploadValidation';

interface HighlightedHtmlElementProps {
    element: IElementHtml;
    highlightedElementChangeHandler: {single: (field: keyof IElementHtml, value: unknown) => void};
}

const lowlight = createLowlight(all);
lowlight.register('html', html);
lowlight.register('css', css);

function HtmlEditorModal({highlightedElementChangeHandler, element}: HighlightedHtmlElementProps): JSX.Element {
    const [editorWidth, setEditorWidth] = useState(50);
    const [slotRoots, setSlotRoots] = useState<Record<string, HTMLElement>>({});
    const openHtmlEditor = useSelector(showHtmlCodeEditorModal);
    const slide = useSelector(selectedSlideSelector);
    const presentation = useSelector(selectedPresentationSelector);
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const slottedElements = (slide?.elements ?? []).filter(isHtmlSlottedElement);
    const reportSlides = useMemo(() => {
        const all = presentation?.slides ?? [];
        if (!slide) return all;
        return [slide, ...all.filter((item) => item._id !== slide._id)];
    }, [presentation?.slides, slide]);
    const rewriteRequest = useRef(0);
    const [uploadHtmlImages] = useState(createHtmlImageUploader);

    const handleDocumentReady = useCallback((doc: Document) => {
        const next: Record<string, HTMLElement> = {};
        doc.querySelectorAll<HTMLElement>('[data-incase-slot]').forEach((node) => {
            const slotId = node.getAttribute('data-slot-id');
            if (slotId) next[slotId] = node;
        });
        setSlotRoots(next);
    }, []);

    const {containerRef} = useHtmlIframe(element, handleDocumentReady);

    const debouncedChangeHandler = useDebouncedCallback(async (content: string, currentEditor: Editor, requestId: number) => {
        try {
            if (!htmlContainsImageDataUrl(content)) {
                highlightedElementChangeHandler.single('text', content);
                return;
            }
            const rewritten = await uploadHtmlImages(content);
            if (requestId !== rewriteRequest.current || currentEditor.isDestroyed) return;
            if (rewritten !== content) {
                currentEditor.commands.setContent(formatHtml(rewritten), false);
            }
            highlightedElementChangeHandler.single('text', rewritten);
        } catch (error) {
            if (requestId !== rewriteRequest.current || currentEditor.isDestroyed) return;
            toast.error(getImageSizeErrorMessage(error) ?? t('common.uploadFailed'));
        }
    }, 250);

    const editor = useEditor({
        extensions: [
            CodeBlockLowlight.configure({
                lowlight,
            }),
            StarterKit.configure({
                codeBlock: false,
            }),
            CodeBlock.configure({
                defaultLanguage: 'html',
                exitOnTripleEnter: false,
                exitOnArrowDown: false,
            }),
        ],
        content: formatHtml(element?.text || ''),
        onUpdate: ({editor}) => {
            rewriteRequest.current += 1;
            debouncedChangeHandler(editor.getText(), editor, rewriteRequest.current);
        },
        onDestroy: () => {
            rewriteRequest.current += 1;
            debouncedChangeHandler.cancel();
        },
        autofocus: 'end',
        onCreate: ({editor}) => {
            editor.commands.setCodeBlock();
        },
        editorProps: {
            attributes: {
                class: 'prose prose-sm focus:outline-none',
                spellcheck: 'false',
            },
        },
    });

    const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
        e.preventDefault();

        const startX = e.clientX;
        const initialEditorWidth = editorWidth;

        const handleMouseMove = (moveEvent: MouseEvent): void => {
            const deltaX = moveEvent.clientX - startX;
            const newEditorWidth = Math.min(Math.max(initialEditorWidth + (deltaX / window.innerWidth) * 100, 10), 90);
            setEditorWidth(newEditorWidth);
        };

        const handleMouseUp = (): void => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };

        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <Modal
            isOpen={openHtmlEditor}
            onCancel={() => {
                dispatch(showHtmlEditorModalAction());
                editor?.destroy();
            }}
            size="fit"
        >
            <div className="c-code_editor_modal">
                <div
                    style={{
                        width: `${editorWidth}%`,
                    }}
                    className="c-code_editor_modal__code-editor"
                >
                    <EditorContent editor={editor} className="c-code_editor ql-editor" />
                </div>

                <div className="c-code_editor_modal__line-divider" onMouseDown={handleMouseDown} />

                <div
                    style={{
                        width: `${100 - editorWidth}%`,
                    }}
                    className="c-code_editor_modal__html-preview"
                >
                    <div ref={containerRef} />
                    {slottedElements.map((slotted) => {
                        const root = slotRoots[slotted.htmlSlotId];
                        if (!root) return null;
                        if (isHtmlQuestionReportElement(slotted)) {
                            return (
                                <HtmlQuestionReportBridge
                                    key={slotted.id}
                                    element={slotted}
                                    slot={root}
                                    slides={reportSlides}
                                    slideName={slide?.name ?? ''}
                                />
                            );
                        }
                        return createPortal(<HtmlSlottedElementPortal element={slotted} />, root, slotted.id);
                    })}
                </div>
            </div>
        </Modal>
    );
}

export default HtmlEditorModal;
